import { useState } from 'react';
import { Loader2 } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/shared/shadcn/ui/dialog';
import { formatDateOnlySafe } from '~shared/helpers/datetime';
import { Button } from '~shared/shadcn/ui/button';

interface StudentMatchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existingStudent: {
    _id: string;
    firstName: string;
    lastName: string;
    dob?: string;
  };
  newStudentName: string;
  onUseExisting: () => Promise<void>;
  onCreateNew: () => Promise<void>;
  isPending?: boolean;
}

export function StudentMatchDialog({
  open,
  onOpenChange,
  existingStudent,
  newStudentName,
  onUseExisting,
  onCreateNew,
  isPending = false,
}: StudentMatchDialogProps) {
  const [action, setAction] = useState<'existing' | 'new' | null>(null);

  const handleUseExisting = async () => {
    setAction('existing');
    try {
      await onUseExisting();
      onOpenChange(false);
    } finally {
      setAction(null);
    }
  };

  const handleCreateNew = async () => {
    setAction('new');
    try {
      await onCreateNew();
      onOpenChange(false);
    } finally {
      setAction(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[425px]'>
        <DialogHeader>
          <DialogTitle>Student Match Found</DialogTitle>
          <DialogDescription>
            We found a student that looks like a match. Would you like to use
            the existing student or create a new one?
          </DialogDescription>
        </DialogHeader>
        <div className='py-4 space-y-3'>
          <div className='p-3 bg-muted rounded-lg'>
            <p className='text-sm font-medium mb-1'>Existing Student:</p>
            <p className='text-sm'>
              {existingStudent.firstName} {existingStudent.lastName}
              {existingStudent.dob &&
                ` (DOB: ${formatDateOnlySafe(existingStudent.dob, 'M/d/yyyy')})`}
            </p>
          </div>
          <div className='p-3 bg-muted rounded-lg'>
            <p className='text-sm font-medium mb-1'>New Student:</p>
            <p className='text-sm'>{newStudentName}</p>
          </div>
        </div>
        <DialogFooter>
          <Button
            type='button'
            variant='outline'
            onClick={handleUseExisting}
            disabled={isPending || action === 'new'}
          >
            {action === 'existing' && (
              <Loader2 className='mr-2 h-4 w-4 animate-spin' />
            )}
            Use Existing
          </Button>
          <Button
            type='button'
            onClick={handleCreateNew}
            disabled={isPending || action === 'existing'}
          >
            {action === 'new' && (
              <Loader2 className='mr-2 h-4 w-4 animate-spin' />
            )}
            Create New
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

